/* eslint-disable @typescript-eslint/no-unused-vars */
import type { MessageInstance } from 'antd/es/message/interface';
import type { QueryClient } from '@tanstack/react-query';
import { btlApplicationFormApi } from '@api/btlApplicationForm/btlApplicationFormApi';
import type { BtlApplication, BtlApplicationType } from '@api/btlApplicationForm/types';
import { CreateCaseDto, RefinancePurpose } from '@api/btlApplicationForm/types';
import type { ExistingFirmResponse } from '@api/firm/firm.types';
import { mapFormSubmissionFunnelTypeToApi } from '@pages/ApplicationFormPage/Submission/submissionRouteHelpers';
import { getPageURLWithSection, PageURL } from '@router/pageURL';
import { config } from '@utils/config';
import { buildIntroducerPayloadFromFormValues } from './btlIntroducerHelpers';
import { buildUpdateFinanceAndPropertyDto, normalizeIntermediaryFees } from './buildUpdateFinanceAndPropertyDto';
import { mapApplicationToFormValues } from './mapApplicationToFormValues';
import { invalidateBtlApplicationData } from '../btl-utils';

interface SubmitFinancingAndPropertyFormParams {
  values: Record<string, any>;
  data?: BtlApplication;
  firm?: ExistingFirmResponse | null;
  id: string;
  propertyId?: string;
  nextSection: string;
  navigate: (path: string) => void;
  onNavigateAfterSubmit?: (nextSection: string) => void;
  onNavigateAfterCreate?: (appId: string, nextSection: string) => void;
  applicationType: BtlApplicationType;
  queryClient?: QueryClient;
  setIsSubmitting: (isSubmitting: boolean) => void;
  message: MessageInstance;
  showSuccessMessage?: boolean;
  includeDepositSources?: boolean;
}

function normalizeFeesAddedToBalance(value: unknown, fallback: boolean): boolean {
  if (Array.isArray(value)) {
    return value[0] ?? false;
  }

  if (typeof value === 'boolean') {
    return value;
  }

  return fallback;
}

function normalizeTermInMonths(value: unknown, fallbackMonths: number): number {
  if (typeof value !== 'number') {
    return fallbackMonths;
  }

  if (value <= 40) {
    return value * 12;
  }

  return value;
}

export async function submitFinancingAndPropertyForm({
  values,
  data,
  firm,
  id,
  propertyId,
  nextSection,
  navigate,
  onNavigateAfterSubmit,
  onNavigateAfterCreate,
  applicationType,
  queryClient,
  setIsSubmitting,
  message,
  showSuccessMessage = true,
  includeDepositSources = false,
}: SubmitFinancingAndPropertyFormParams) {
  const { env } = config;
  const baseValues = mapApplicationToFormValues(data, firm);
  const mergedValues: Record<string, any> = { ...baseValues, ...values };
  const {
    purpose,
    financingAmount,
    outstandingBalance,
    purchasePrice,
    grossMonthlyRentalPayment,
    expectedPropertyType,
    paymentProfile,
    repaymentStrategy,
    repaymentStrategyDetails,
    refinancePurpose,
    feesAddedToBalance,
    termInMonths,
    submissionFunnelType: _submissionFunnelType,
    hasIntroducer: _hasIntroducer,
    introducerFirstName: _introducerFirstName,
    introducerLastName: _introducerLastName,
    introducerEmail: _introducerEmail,
    introducerPhoneNumber: _introducerPhoneNumber,
    hasIntermediaryFees: _hasIntermediaryFees,
    fees: _fees,
    region: _region,
    ...rest
  } = mergedValues;

  const submissionFunnelType = mapFormSubmissionFunnelTypeToApi(mergedValues.submissionFunnelType);
  const introducer = firm ? buildIntroducerPayloadFromFormValues(mergedValues, firm) : undefined;
  const intermediaryFees = normalizeIntermediaryFees(mergedValues.hasIntermediaryFees, mergedValues.fees);
  const isOwnerOccupiedCreate = applicationType === 'OWNER_OCCUPIED';

  try {
    const resolvedPurpose = purpose ?? data?.purpose;
    const resolvedRefinancePurpose =
      resolvedPurpose === 'PURCHASE' ? null : refinancePurpose ?? data?.refinancePurpose ?? null;

    setIsSubmitting(true);

    if (id) {
      const updateCaseDto = buildUpdateFinanceAndPropertyDto({
        caseId: id,
        propertyId,
        mergedValues,
        data,
        firm,
        includeDepositSources,
      });

      await btlApplicationFormApi.updateFinanceAndProperty(id, updateCaseDto);

      if (queryClient) {
        await invalidateBtlApplicationData(queryClient, id);
      }

      if (showSuccessMessage) {
        message.success(`Application updated${env.VITE_ENV === 'production' ? '' : ` with id: ${id}`}`);
      }
      if (onNavigateAfterSubmit) {
        onNavigateAfterSubmit(nextSection);
      } else {
        navigate(getPageURLWithSection(PageURL.BTL_APPLICATION_EDIT, { id }, { section: nextSection }));
      }
      return;
    }

    const createCaseDto = {
      purpose: resolvedPurpose,
      financingAmount,
      outstandingBalance:
        resolvedRefinancePurpose === RefinancePurpose.LIKE_FOR_LIKE ? financingAmount : outstandingBalance,
      ...rest,
      refinancePurpose: resolvedRefinancePurpose,
      feesAddedToBalance: normalizeFeesAddedToBalance(feesAddedToBalance, false),
      termInMonths: normalizeTermInMonths(termInMonths, 0),
      paymentProfile: isOwnerOccupiedCreate ? paymentProfile ?? 'ACQUISITION' : paymentProfile,
      repaymentStrategy: isOwnerOccupiedCreate ? repaymentStrategy ?? null : repaymentStrategy,
      repaymentStrategyDetails: isOwnerOccupiedCreate ? repaymentStrategyDetails ?? null : repaymentStrategyDetails,
      property: {
        purchasePrice,
        grossMonthlyRentalPayment: grossMonthlyRentalPayment ?? 0,
        expectedPropertyType,
      },
      submissionType: 'INTRODUCED',
      applicationType,
      ...(submissionFunnelType ? { submissionFunnelType } : {}),
      ...(introducer ? { introducer } : {}),
      ...(intermediaryFees !== undefined ? { intermediaryFees } : {}),
    } as unknown as CreateCaseDto;

    const appId = await btlApplicationFormApi.createApplication(createCaseDto);

    if (isOwnerOccupiedCreate) {
      const createdCase = (await btlApplicationFormApi.getApplicationInProgress(appId)) as unknown as BtlApplication;
      const updateCaseDto = buildUpdateFinanceAndPropertyDto({
        caseId: appId,
        propertyId: createdCase.applicationProperty?.property?.id,
        mergedValues,
        data: createdCase,
        firm,
        includeDepositSources,
      });
      await btlApplicationFormApi.updateFinanceAndProperty(appId, updateCaseDto);

      if (showSuccessMessage) {
        message.success(`Application created${env.VITE_ENV === 'production' ? '' : ` with id: ${appId}`}`);
      }

      if (onNavigateAfterCreate) {
        onNavigateAfterCreate(appId, nextSection);
      } else {
        navigate(getPageURLWithSection(PageURL.APPLICATION_DETAILS, { id: appId }, { section: nextSection }));
      }
      return;
    }

    if (showSuccessMessage) {
      message.success(`Application created${env.VITE_ENV === 'production' ? '' : ` with id: ${appId}`}`);
    }
    navigate(getPageURLWithSection(PageURL.BTL_APPLICATION_EDIT, { id: appId }, { section: nextSection }));
  } catch (error: unknown) {
    console.error(error);
    message.error(error instanceof Error ? error.message : 'Failed to save application');
  } finally {
    setIsSubmitting(false);
  }
}
